/**
 * LineItemsTable.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Shared, fully-controlled line items table with:
 *   - Editable rows (details, brand, category, subcategory, qty, gst, pp, sp,
 *     vendor, vendor payment terms)
 *   - Read-only computed columns (nlc, nsp, totalPp, totalSp, margin, margin%)
 *   - Transportation / Customization / Marketing subtotal rows
 *   - Grand total row
 *   - Optional read-only mode (viewOnly prop)
 *   - Catalog search icon in Details column
 *    once a catalog product is picked
 *     (the line has an ESIN) Details is read-only and changes only by picking again
 *   - Custom details: free text under Details (SKU, colour, attributes…), hidden
 *     for internal cost sheets, shown
 *     highlighted in view mode
 *
 * Props
 * ─────
 * items              {Array}    – line item objects
 * onItemChange       {fn}       – (index, field, value) => void
 * onAddItem          {fn}       – () => void
 * onRemoveItem       {fn}       – (index) => void
 * subtotals          {Object}   – { transportationTotalPp, transportationTotalSp,
 *                                   customizationTotalPp, customizationTotalSp,
 *                                   marketingTotalPp, marketingTotalSp }
 * onSubtotalChange   {fn}       – (field, value) => void
 * brands             {Array}
 * categories         {Array}
 * categorySubcategoryMap {Object}
 * catalogLoading     {boolean}
 * customVendorTerms  {Object}   – { [rowIndex]: string }
 * onCustomVendorTermChange {fn} – (index, value) => void
 * onOpenCatalog      {fn}       – (index) => void   (omit to hide search icon)
 * qtyErrors          {Object}   – { [rowIndex]: string }
 * onQtyErrorChange   {fn}       – (index, errorMsg) => void
 * validated          {boolean}  – shows required field errors
 * viewOnly           {boolean}  – disables all inputs (default false)
 */

import React from 'react';
import {
  Box, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Paper, TextField, Typography, IconButton,
  FormControl, InputLabel, Select, MenuItem, Autocomplete,
  Tooltip, Chip, InputAdornment,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon    from '@mui/icons-material/DeleteOutline';
import SearchIcon            from '@mui/icons-material/Search';
import LocalShippingIcon     from '@mui/icons-material/LocalShipping';
import BrushIcon             from '@mui/icons-material/Brush';
import CampaignIcon          from '@mui/icons-material/Campaign';
import TrendingUpIcon        from '@mui/icons-material/TrendingUp';
import InfoOutlinedIcon      from '@mui/icons-material/InfoOutlined';
import LockOutlinedIcon      from '@mui/icons-material/LockOutlined';
import { Button }            from '@mui/material';
import { alpha }             from '@mui/system';

import {
  COLORS, FONT, FIELD_SX, SELECT_SX,
  TH_SX, DataRow, SubtotalRow, TotalRow,
  CurrencyDisplay, MarginBadge, MarginChip,
  PAYMENT_TERM_OPTIONS, formatCurrency,
} from './DesignSystem';

// ─── Helpers ─────────────────────────────────────────────────────────────────

// Custom details are highlighted wherever a line is viewed
const CUSTOM_DETAILS_SX = {
  mt: 0.75, px: 1, py: 0.5,
  bgcolor: '#FEF9C3', border: '1px solid #FDE68A', borderRadius: '6px',
  color: '#713F12', fontSize: 12, lineHeight: 1.45,
  whiteSpace: 'pre-wrap', wordBreak: 'break-word',
};

// A line whose product was picked from the catalog keeps the catalog's details, brand, category
// and subcategory; they're read-only and change only by picking another product
// True when a cost sheet moves into or out of Internal, where line products don't carry over
export const isInternalSwitch = (from, to) =>
  (from?.toLowerCase() === 'internal') !== (to?.toLowerCase() === 'internal');

const isCatalogProduct = (item) => !!(item.esin && String(item.esin).trim());
const LOCKED_FIELD_SX = { '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: 13, bgcolor: COLORS.surface } };
const fieldSx = (locked) => (locked ? LOCKED_FIELD_SX : FIELD_SX);

const calc = (pp, sp, gst, qty) => {
  const nlc    = pp * (1 + gst / 100);
  const nsp    = sp * (1 + gst / 100);
  const totPP  = pp * qty; // exclude gst
  const totSP  = sp * qty; // exclude gst
  const totPPGST = nlc * qty; // Include GST
  const totSPGST = nsp * qty; // Include GST
  const margin = totSP - totPP;
  const pct    = totPP !== 0 ? (margin / totPP) * 100 : 0;
  return { nlc, nsp, totPP, totSP, margin, pct };
};

const SubRowIcon = ({ label }) => {
  if (label === 'Transportation') return <LocalShippingIcon sx={{ fontSize: 14, color: COLORS.textMuted }} />;
  if (label === 'Customization')  return <BrushIcon         sx={{ fontSize: 14, color: COLORS.textMuted }} />;
  return                                 <CampaignIcon       sx={{ fontSize: 14, color: COLORS.textMuted }} />;
};

// ─── Component ───────────────────────────────────────────────────────────────

const LineItemsTable = ({
  items = [],
  onItemChange,
  onAddItem,
  onRemoveItem,
  subtotals = {},
  onSubtotalChange,
  brands = [],
  categories = [],
  categorySubcategoryMap = {},
  catalogLoading = false,
  customVendorTerms = {},
  onCustomVendorTermChange,
  onOpenCatalog,
  qtyErrors = {},
  onQtyErrorChange,
  validated = false,
  viewOnly = false,
  isInternal = false,
  customRows = [],
  onCustomRowChange,
  legalVendors = [],
  onLegalVendorChange,
}) => {

  const {
    transportationTotalPp = '0', transportationTotalSp = '0',
    customizationTotalPp  = '0', customizationTotalSp  = '0',
    marketingTotalPp      = '0', marketingTotalSp      = '0',
    transportationGST     = '0',
    customizationGST      = '0',
    marketingGST          = '0',
  } = subtotals;

  // Grand total
  const itemsPP  = items.reduce((s, i) => s + (parseFloat(i.totalPp) || 0), 0);
  const itemsSP  = items.reduce((s, i) => s + (parseFloat(i.totalSp) || 0), 0);

  const customPPGST = customRows.reduce((s, row) => {
    const pp = parseFloat(row.customTotalPp) || 0;
    const gst = parseFloat(row.customGST) || 0;
    return s + (pp * (1 + gst / 100));
  }, 0);

  const customSPGST = customRows.reduce((s, row) => {
    const sp = parseFloat(row.customTotalSp) || 0;
    const gst = parseFloat(row.customGST) || 0;
    return s + (sp * (1 + gst / 100));
  }, 0);
  const itemsPPGST = items.reduce((s,i)=> s +(parseFloat(i.totPPGST) || 0), 0) + customPPGST;
  const itemsSPGST = items.reduce((s,i)=> s +(parseFloat(i.totSPGST) || 0), 0) + customSPGST;
  const customPP = customRows.reduce((s, r) => s + (parseFloat(r.customTotalPp) || 0), 0);
  const customSP = customRows.reduce((s, r) => s + (parseFloat(r.customTotalSp) || 0), 0);
  const grandPP  = itemsPP  + (+transportationTotalPp || 0) + (+customizationTotalPp || 0) + (+marketingTotalPp || 0) + customPP || 0;
  const grandSP  = itemsSP  + (+transportationTotalSp || 0) + (+customizationTotalSp || 0) + (+marketingTotalSp || 0) + customSP || 0;
  
  const grandMg  = grandSP  - grandPP;
  const grandPct = grandPP  !== 0 ? ((grandMg / grandPP) * 100).toFixed(1) : '0.0';

  const transGST = parseFloat(transportationGST) || 0;
  const custGST  = parseFloat(customizationGST)  || 0;
  const mktGST   = parseFloat(marketingGST)       || 0;

  const grandPPGST = itemsPPGST
    + ((parseFloat(transportationTotalPp) || 0) * (1 + transGST / 100))
    + ((parseFloat(customizationTotalPp) || 0) * (1 + custGST / 100))
    + ((parseFloat(marketingTotalPp) || 0) * (1 + mktGST / 100));

  const grandSPGST = itemsSPGST
    + ((parseFloat(transportationTotalSp) || 0) * (1 + transGST / 100))
    + ((parseFloat(customizationTotalSp) || 0) * (1 + custGST / 100))
    + ((parseFloat(marketingTotalSp) || 0) * (1 + mktGST / 100));

  const subRows = [
    { label: 'Transportation', ppKey: 'transportationTotalPp', spKey: 'transportationTotalSp', pp: transportationTotalPp, sp: transportationTotalSp, gstKey: 'transportationGST', gst: transportationGST },
    { label: 'Customization',  ppKey: 'customizationTotalPp',  spKey: 'customizationTotalSp',  pp: customizationTotalPp,  sp: customizationTotalSp,  gstKey: 'customizationGST', gst: customizationGST },
    { label: 'Marketing',      ppKey: 'marketingTotalPp',      spKey: 'marketingTotalSp',      pp: marketingTotalPp,      sp: marketingTotalSp,  gstKey: 'marketingGST', gst: marketingGST },
  ];

  const fmtInput = (v) => ({ fontSize: 13, padding: '6px 8px' });
  // ─── Custom Row Handlers ──────────────────────────────────────────────────
  const handleAddCustomRow = () => {
    const updated = [
      ...(customRows || []),
      { customLabel: '', customTotalPp: '0', customTotalSp: '0', customGST: '0' },
    ];
    onCustomRowChange?.(updated);
  };

    const handleCustomRowChange = (index, field, value) => {
      const updated = customRows.map((row, i) =>
        i === index ? { ...row, [field]: value } : row
      );
      onCustomRowChange?.(updated);
    };

    const handleRemoveCustomRow = (index) => {
      const updated = customRows.filter((_, i) => i !== index);
      onCustomRowChange?.(updated);
    };


  return (
    <Box>
      <TableContainer sx={{ maxHeight: 520, overflowX: 'auto', overflowY: 'auto' }}>
        <Table stickyHeader sx={{ minWidth: 1500 }}>

          {/* ── HEAD ── */}
          <TableHead>
            <TableRow>
              {[
                ['Details',          180],
                ['Brand',            130],
                ['Category',         130],
                ['Subcategory',      130],
                ['Qty',               60],
                ['GST %',             60],
                ['PP (₹)',            90],
                ['NLC',               80],
                ['SP (₹)',            90],
                ['NSP',               80],
                ['Total PP',         100],
                ['Total SP',         100],
                ['Margin',            90],
                ['Margin %',          80],
                ['Total PP (Incl GST)',          80],
                ['Total SP (Incl GST)',          80],
                ['Vendor',           220],
                ['Vendor Terms',     148],
                ...(viewOnly ? [] : [['', 48]]),
              ].map(([col, minW]) => (
                <TableCell key={col} sx={{ ...TH_SX, minWidth: minW }}>{col}</TableCell>
              ))}
            </TableRow>
          </TableHead>

          {/* ── BODY ── */}
          <TableBody>
            {items.map((item, index) => (
              <DataRow key={index} rowindex={index}>

                {/* Details */}
                <TableCell>
                  {viewOnly ? (
                    <>
                      <Typography variant="body2" fontWeight={600} fontSize={13}>{item.details}</Typography>
                      {item.customDetails?.trim() && (
                        <Box sx={CUSTOM_DETAILS_SX}>{item.customDetails}</Box>
                      )}
                    </>
                  ) : (
                    <>
                      <TextField
                        fullWidth size="small" value={item.details || ''}
                        onChange={(e) => onItemChange(index, 'details', e.target.value)}
                        // Internal lines start blank, so flag a missing product name straight away
                        required error={(validated || isInternal) && !item.details?.trim()}
                        placeholder={isInternal ? 'Product name (required)' : 'Product name'}
                        sx={fieldSx(isCatalogProduct(item))}
                        InputProps={{
                          readOnly: isCatalogProduct(item),
                          ...(isCatalogProduct(item) && {
                            startAdornment: (
                              <InputAdornment position="start">
                                <Tooltip title="Selected from the catalog. Use search to pick a different product." placement="top">
                                  <LockOutlinedIcon sx={{ fontSize: 14, color: COLORS.textMuted }} />
                                </Tooltip>
                              </InputAdornment>
                            ),
                          }),
                          ...(!isInternal && onOpenCatalog && {
                            endAdornment: (
                              <InputAdornment position="end">
                                <Tooltip title={isCatalogProduct(item) ? 'Change catalog product' : 'Search catalog'} placement="top">
                                  <IconButton edge="end" size="small" onClick={() => onOpenCatalog(index)}>
                                    <SearchIcon sx={{ fontSize: 16, color: COLORS.indigo }} />
                                  </IconButton>
                                </Tooltip>
                              </InputAdornment>
                            ),
                          }),
                        }}
                      />
                      {!isInternal && (
                      <TextField
                        fullWidth size="small" multiline minRows={1} maxRows={4}
                        value={item.customDetails || ''}
                        onChange={(e) => onItemChange(index, 'customDetails', e.target.value)}
                        placeholder="Custom details (SKU, colour, attributes…)"
                        sx={{ ...FIELD_SX, mt: 0.75, '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: 12, bgcolor: '#FFFDF0' } }}
                        inputProps={{ 'aria-label': 'Custom details' }}
                      />
                      )}
                    </>
                  )}
                </TableCell>

                {/* Brand */}
                <TableCell>
                  {viewOnly ? (
                    <Typography variant="body2" fontSize={12} color="text.secondary">{item.brand?.name || '—'}</Typography>
                  ) : (
                    <Autocomplete
                      options={brands}
                      disabled={isInternal}
                      readOnly={isCatalogProduct(item)}
                      getOptionLabel={(o) => o.name || ''}
                      value={item.brand || null}
                      isOptionEqualToValue={(o, v) => o.name === v.name || o.id === v.id}
                      onChange={(_, v) => onItemChange(index, 'brand', v)}
                      loading={catalogLoading}
                      size="small"
                      renderInput={(p) => <TextField {...p} placeholder="Brand" sx={fieldSx(isCatalogProduct(item))} />}
                    />
                  )}
                </TableCell>

                {/* Category */}
                <TableCell>
                  {viewOnly ? (
                    <Typography variant="body2" fontSize={12} color="text.secondary">{item.category?.name || '—'}</Typography>
                  ) : (
                    <Autocomplete
                      options={categories}
                      disabled={isInternal}
                      readOnly={isCatalogProduct(item) && !!item.category}
                      getOptionLabel={(o) => o.name || ''}
                      value={item.category || null}
                      isOptionEqualToValue={(o, v) => o.name === v.name || o.id === v.id}
                      onChange={(_, v) => onItemChange(index, 'category', v)}
                      loading={catalogLoading}
                      size="small"
                      renderInput={(p) => (
                        <TextField {...p} placeholder="Category" required error={validated && !item.category} sx={fieldSx(isCatalogProduct(item) && !!item.category)} />
                      )}
                    />
                  )}
                </TableCell>

                {/* Subcategory */}
                <TableCell>
                  {viewOnly ? (
                    <Typography variant="body2" fontSize={12} color="text.secondary">{item.subcategory?.name || '—'}</Typography>
                  ) : (
                    <Autocomplete
                      options={item.category ? (categorySubcategoryMap[item.category.id || item.category.name] || []) : []}
                      getOptionLabel={(o) => o.name || ''}
                      value={item.subcategory || null}
                      isOptionEqualToValue={(o, v) => o.name === v.name || o.id === v.id}
                      onChange={(_, v) => onItemChange(index, 'subcategory', v)}
                      disabled={!item.category || isInternal}
                      readOnly={isCatalogProduct(item) && !!item.subcategory}
                      loading={catalogLoading}
                      size="small"
                      renderInput={(p) => (
                        <TextField {...p} placeholder="Subcategory" required error={validated && !item.subcategory} sx={fieldSx(isCatalogProduct(item) && !!item.subcategory)} />
                      )}
                    />
                  )}
                </TableCell>

                {/* Qty */}
                <TableCell>
                  {viewOnly ? (
                    <Typography variant="body2" fontWeight={700} fontSize={13} textAlign="center">{item.qty}</Typography>
                  ) : (
                    <TextField
                      fullWidth size="small" type="text"
                      inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                      value={item.qty}
                      onChange={(e) => {
                        let v = e.target.value;
                        let err = '';
                        if (v.includes('.')) { err = 'No decimals'; v = v.split('.')[0]; }
                        v = v.replace(/[^0-9]/g, '');
                        onItemChange(index, 'qty', v);
                        onQtyErrorChange?.(index, err);
                      }}
                      required
                      error={!!qtyErrors[index] || (validated && !item.qty)}
                      helperText={qtyErrors[index] || (validated && !item.qty ? 'Required' : '')}
                      sx={FIELD_SX}
                    />
                  )}
                </TableCell>

                {/* GST */}
                <TableCell>
                  {viewOnly ? (
                    <Typography variant="body2" fontSize={13}>{item.gst}%</Typography>
                  ) : (
                    <TextField
                      fullWidth size="small" value={item.gst}
                      onChange={(e) => onItemChange(index, 'gst', e.target.value)}
                      required error={validated && item.gst === ''}
                      placeholder="0"
                      sx={FIELD_SX}
                      InputProps={{ endAdornment: <InputAdornment position="end" sx={{ fontSize: 12 }}>%</InputAdornment> }}
                    />
                  )}
                </TableCell>

                {/* PP */}
                <TableCell>
                  {viewOnly ? (
                    <CurrencyDisplay value={item.pp} />
                  ) : (
                    <TextField
                      fullWidth size="small" value={item.pp}
                      onChange={(e) => onItemChange(index, 'pp', e.target.value)}
                      required error={validated && !item.pp}
                      placeholder="0.00"
                      sx={FIELD_SX}
                      InputProps={{ startAdornment: <InputAdornment position="start" sx={{ fontSize: 12 }}>₹</InputAdornment> }}
                    />
                  )}
                </TableCell>

                {/* NLC (read-only) */}
                <TableCell>
                  <Typography variant="body2" sx={{ color: COLORS.textMuted, fontFamily: FONT.mono, fontSize: 13 }}>
                    {(formatCurrency(item.nlc) || '—')}
                  </Typography>
                </TableCell>

                {/* SP */}
                <TableCell>
                  {viewOnly ? (
                    <CurrencyDisplay value={item.sp} />
                  ) : (
                    <TextField
                      fullWidth size="small" value={item.sp}
                      onChange={(e) => onItemChange(index, 'sp', e.target.value)}
                      required error={validated && !item.sp}
                      placeholder="0.00"
                      sx={FIELD_SX}
                      InputProps={{ startAdornment: <InputAdornment position="start" sx={{ fontSize: 12 }}>₹</InputAdornment> }}
                    />
                  )}
                </TableCell>

                {/* NSP (read-only) */}
                <TableCell>
                  <Typography variant="body2" sx={{ color: COLORS.textMuted, fontFamily: FONT.mono, fontSize: 13 }}>
                    {(formatCurrency(item.nsp) || '—')}
                  </Typography>
                </TableCell>

                {/* Total PP */}
                <TableCell><CurrencyDisplay value={item.totalPp} /></TableCell>

                {/* Total SP */}
                <TableCell><CurrencyDisplay value={item.totalSp} /></TableCell>

                {/* Margin */}
                <TableCell><CurrencyDisplay value={item.margin} highlight /></TableCell>

                {/* Margin % */}
                <TableCell><MarginBadge percent={item.marginPercent} /></TableCell>

                {/**Total PP Incl GST */}
                <TableCell>
                {viewOnly ? (
                    <CurrencyDisplay value={item.totPPGST} />
                  ) : (
                    <TextField
                      fullWidth size="small" value={item.totPPGST}
                      onChange={(e) => onItemChange(index, 'totPPGST', e.target.value)}
                      required error={validated && !item.totPPGST}
                      placeholder="0.00"
                      disabled
                      sx={FIELD_SX}
                      InputProps={{ startAdornment: <InputAdornment position="start" sx={{ fontSize: 12 }}>₹</InputAdornment> }}
                    />
                  )}
                  </TableCell>
                <TableCell>
                {viewOnly ? (
                    <CurrencyDisplay value={item.totSPGST} />
                  ) : (
                    <TextField
                      fullWidth size="small" value={item.totSPGST}
                      onChange={(e) => onItemChange(index, 'totSPGST', e.target.value)}
                      required error={validated && !item.sp}
                      placeholder="0.00"
                      disabled
                      sx={FIELD_SX}
                      InputProps={{ startAdornment: <InputAdornment position="start" sx={{ fontSize: 12 }}>₹</InputAdornment> }}
                    />
                  )}
                </TableCell>

                {/* Vendor */}
                <TableCell>
                  {viewOnly ? (
                    <Box>
                      <Typography variant="body2" fontSize={12}>
                        {item.legalVendorId === 'custom'
                          ? item.vendorName
                          : (legalVendors.find(v => v.id === item.legalVendorId)?.legalVendorName || item.vendorName)}
                      </Typography>
                      {item.poNumber && (
                        <Typography variant="caption" color="text.secondary">PO: {item.poNumber}</Typography>
                      )}
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      <Autocomplete
                        options={isInternal ? [{ id: 'custom', legalVendorName: 'Custom (type manually)' }, ...legalVendors] : legalVendors}
                        getOptionLabel={(o) => o.legalVendorName || ''}
                        value={
                          item.legalVendorId === 'custom'
                            ? { id: 'custom', legalVendorName: 'Custom (type manually)' }
                            : legalVendors.find(v => v.id === item.legalVendorId) || null
                        }
                        isOptionEqualToValue={(o, v) => o?.id != null && v?.id != null && String(o.id) === String(v.id)}
                        // Vendors registered without GST are listed but can't be picked
                        getOptionDisabled={(o) => o.id !== 'custom' && !o.vendorGST?.trim()}
                        onChange={(_, newVal) => onLegalVendorChange?.(index, newVal)}
                        size="small"
                        renderOption={(props, option) =>
                          option.id === 'custom' ? (
                            <Box component="li" {...props} key="custom-vendor-option">
                              <Typography variant="body2" fontWeight={700} fontSize={13} sx={{ color: COLORS.indigo }}>
                                + Custom Vendor
                              </Typography>
                            </Box>
                          ) : (
                            <Box component="li" {...props} key={option.id} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start !important', py: 1 }}>
                              <Typography variant="body2" fontWeight={600} fontSize={13}>{option.legalVendorName || option.vendorName}</Typography>
                              <Box sx={{ display: 'flex', gap: 1.5, mt: 0.25 }}>
                                {option.vendorGST
                                  ? <Typography variant="caption" sx={{ color: COLORS.textMuted, fontFamily: FONT.mono, fontSize: 11 }}>GST: {option.vendorGST}</Typography>
                                  : <Typography variant="caption" sx={{ color: COLORS.danger, fontWeight: 600, fontSize: 11 }}>GST not registered</Typography>}
                                {option.vendorState && <Typography variant="caption" sx={{ color: COLORS.textMuted, fontSize: 11 }}>{option.vendorState}</Typography>}
                              </Box>
                            </Box>
                          )
                        }
                        renderInput={(params) => <TextField {...params} placeholder="Legal Vendor" sx={FIELD_SX} />}
                      />

                      {item.legalVendorId === 'custom' && (
                        <TextField
                          fullWidth size="small"
                          placeholder="Enter vendor name"
                          value={item.vendorName || ''}
                          onChange={(e) => onItemChange(index, 'vendorName', e.target.value)}
                          required
                          error={(validated?? false) && !item.vendorName}
                          sx={FIELD_SX}
                        />
                      )}

                      {!item.legalVendorId && item.vendorName  && !isInternal && (
                        <Typography variant="caption" sx={{ color: COLORS.textMuted, fontStyle: 'italic', fontSize: 11 }}>
                          Previous vendor: {item.vendorName}
                        </Typography>
                      )}
                    </Box>
                  )}
                </TableCell>

                {/* Vendor Payment Terms */}
                <TableCell>
                  {viewOnly ? (
                    <Typography variant="body2" fontSize={12}>{item.vendorPaymentTerms || '—'}</Typography>
                  ) : (
                    <>
                      <FormControl
                        fullWidth size="small"
                        required error={validated && !item.vendorPaymentTerms}
                        sx={FIELD_SX}
                      >
                        <Select
                          displayEmpty
                          value={item.vendorPaymentTerms}
                          onChange={(e) => onItemChange(index, 'vendorPaymentTerms', e.target.value)}
                          sx={{ ...SELECT_SX, fontSize: 12 }}
                          renderValue={(v) =>
                            v
                              ? (PAYMENT_TERM_OPTIONS.find(o => o.value === v)?.label || v)
                              : <span style={{ color: COLORS.textMuted, fontSize: 12 }}>Terms…</span>
                          }
                        >
                          {PAYMENT_TERM_OPTIONS.map(({ value, label }) => (
                            <MenuItem key={value} value={value} sx={{ fontSize: 13 }}>{label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {item.vendorPaymentTerms === 'custom' && (
                        <TextField
                          fullWidth size="small"
                          sx={{ mt: 0.5, ...FIELD_SX }}
                          placeholder="e.g. 90 days net"
                          value={customVendorTerms[index] || ''}
                          onChange={(e) => onCustomVendorTermChange?.(index, e.target.value)}
                          required
                          error={validated && !customVendorTerms[index]}
                        />
                      )}
                    </>
                  )}
                </TableCell>

                {/* Remove */}
                {!viewOnly && (
                  <TableCell sx={{ px: 0.5 }}>
                    <Tooltip title="Remove row" placement="top">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => onRemoveItem(index)}
                          disabled={items.length <= 1}
                          sx={{ color: COLORS.danger, '&:hover': { bgcolor: alpha(COLORS.danger, 0.08) } }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                )}
              </DataRow>
            ))}

            {/* ── CUSTOM ROWS ── */}
            {customRows.map((row, index) => {
              const ppF = parseFloat(row.customTotalPp) || 0;
              const spF = parseFloat(row.customTotalSp) || 0;
              const cGST = parseFloat(row.customGST) ||0;
              const mg  = spF - ppF;
              const ppFGST = ppF * (1 + cGST / 100);
              const spFGST = spF * (1 + cGST / 100);
              const pct = ppF !== 0 ? ((mg / ppF) * 100).toFixed(1) : '0.0';

              return (
                <SubtotalRow key={`custom-${index}`}>

                  {/* Label */}
                  <TableCell colSpan={1}>
                    {viewOnly ? (
                      <Typography variant="body2" fontSize={13}>{row.customLabel || '—'}</Typography>
                    ) : (
                      <TextField
                        fullWidth size="small"
                        placeholder="Custom label"
                        value={row.customLabel}
                        onChange={(e) => handleCustomRowChange(index, 'customLabel', e.target.value)}
                        sx={FIELD_SX}
                      />
                    )}
                  </TableCell>
                  <TableCell colSpan={4}/>
                  {/* Custom GST */}
                  <TableCell colSpan={1}>
                    {viewOnly ? (
                      <Typography variant="body2" fontSize={13}>{row.customGST || '—'}%</Typography>
                    ) : (
                      <TextField
                        fullWidth size="small"
                        placeholder="GST"
                        value={row.customGST}
                        onChange={(e) => handleCustomRowChange(index, 'customGST', e.target.value)}
                        sx={FIELD_SX}
                        InputProps={{ endAdornment: <InputAdornment position="end" sx={{ fontSize: 12 }}>%</InputAdornment> }}
                      />
                    )}
                  </TableCell>

                  <TableCell colSpan={4} />

                  {/* Total PP */}
                  <TableCell>
                    {viewOnly ? (
                      <CurrencyDisplay value={row.customTotalPp} />
                    ) : (
                      <TextField
                        fullWidth size="small"
                        value={row.customTotalPp}
                        onChange={(e) => handleCustomRowChange(index, 'customTotalPp', e.target.value)}
                        sx={FIELD_SX}
                        InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                      />
                    )}
                  </TableCell>
                  
                  {/* Total SP */}
                  <TableCell>
                    {viewOnly ? (
                      <CurrencyDisplay value={row.customTotalSp} />
                    ) : (
                      <TextField
                        fullWidth size="small"
                        value={row.customTotalSp}
                        onChange={(e) => handleCustomRowChange(index, 'customTotalSp', e.target.value)}
                        sx={FIELD_SX}
                        InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                      />
                    )}
                  </TableCell>

                  {/* Margin */}
                  <TableCell>
                    <Typography variant="body2" sx={{
                      color: mg >= 0 ? COLORS.success : COLORS.danger,
                      fontFamily: FONT.mono,
                      fontSize: 13,
                    }}>
                      ₹{formatCurrency(mg)}
                    </Typography>
                  </TableCell>

                  {/* Margin % */}
                  <TableCell>
                    <Typography variant="body2" sx={{ color: COLORS.textMuted, fontSize: 13 }}>{pct}%</Typography>
                  </TableCell>
                  <TableCell colSpan={1}>
                    <Typography variant="body2" sx={{ color: COLORS.textMuted, fontSize: 13 }}>₹{formatCurrency(ppFGST)}</Typography>
                  </TableCell>
                  <TableCell colSpan={1}>
                    <Typography variant="body2" sx={{ color: COLORS.textMuted, fontSize: 13 }}>₹{formatCurrency(spFGST)}</Typography>
                  </TableCell>

                  {/* Remove */}
                  <TableCell colSpan={viewOnly ? 2 : 4 }>
                    {!viewOnly && (
                      <Tooltip title="Remove custom row" placement="top">
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveCustomRow(index)}
                          sx={{ color: COLORS.danger, '&:hover': { bgcolor: alpha(COLORS.danger, 0.08) } }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </SubtotalRow>
              );
            })}

            {/* ── SUBTOTAL ROWS ── */}
            {subRows.map(({ label, ppKey, spKey, pp, sp, gstKey, gst }) => {
              const ppF = parseFloat(pp) || 0;
              const spF = parseFloat(sp) || 0;
              const gstF = parseFloat(gst) || 0;
              const mg  = spF - ppF;
              const pct = ppF !== 0 ? ((mg / ppF) * 100).toFixed(1) : '0.0';
              
              const ppGST = ppF * (1 + gstF / 100);
              const spGST = spF * (1 + gstF / 100);
              return (
                <SubtotalRow key={label}>
                  <TableCell colSpan={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SubRowIcon label={label} />
                      <Typography variant="body2" sx={{ color: COLORS.textMuted, fontStyle: 'italic', fontSize: 13 }}>
                        {label}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell colSpan={1}/>
                  <TableCell colSpan={1}>
                    {viewOnly ? (
                      <Typography variant="body2" fontSize={13} color="text.secondary">
                        {gstF}%
                      </Typography>
                    ) : (
                      <TextField
                        fullWidth size="small"
                        value={gst}
                        onChange={(e) => onSubtotalChange(gstKey, e.target.value)}
                        placeholder="0"
                        sx={{ ...FIELD_SX, '& input': { color: COLORS.textMuted } }}
                        InputProps={{
                          endAdornment: <InputAdornment position="end" sx={{ fontSize: 11 }}>%</InputAdornment>
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell colSpan={4} />

                  {/* Total PP */}
                  <TableCell>
                    {viewOnly ? (
                      <CurrencyDisplay value={pp} />
                    ) : (
                      <TextField fullWidth size="small" value={pp}
                        onChange={(e) => onSubtotalChange(ppKey, e.target.value)}
                        sx={{ ...FIELD_SX, '& input': { color: COLORS.textMuted } }}
                        InputProps={{ startAdornment: <InputAdornment position="start" sx={{ fontSize: 11 }}>₹</InputAdornment> }}
                      />
                    )}
                  </TableCell>

                  {/* Total SP */}
                  <TableCell>
                    {viewOnly ? (
                      <CurrencyDisplay value={sp} />
                    ) : (
                      <TextField fullWidth size="small" value={sp}
                        onChange={(e) => onSubtotalChange(spKey, e.target.value)}
                        sx={{ ...FIELD_SX, '& input': { color: COLORS.textMuted } }}
                        InputProps={{ startAdornment: <InputAdornment position="start" sx={{ fontSize: 11 }}>₹</InputAdornment> }}
                      />
                    )}
                  </TableCell>

                  {/* Margin */}
                  <TableCell>
                    <Typography variant="body2" sx={{ color: mg >= 0 ? COLORS.success : COLORS.danger, fontFamily: FONT.mono, fontSize: 13 }}>
                      ₹{formatCurrency(mg)}
                    </Typography>
                  </TableCell>

                  {/* Margin % */}
                  <TableCell>
                    <Typography variant="body2" sx={{ color: COLORS.textMuted, fontSize: 13 }}>{pct}%</Typography>
                  </TableCell>
                  
                  <TableCell>
                    <Typography variant="body2" sx={{ color: COLORS.textMuted, fontSize: 13 }}>₹{formatCurrency(ppGST)}</Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2" sx={{ color: COLORS.textMuted, fontSize: 13 }}>₹{formatCurrency(spGST)}</Typography>
                  </TableCell>


                  <TableCell colSpan={viewOnly ? 2 : 3} />
                </SubtotalRow>
              );
            })}

            {/* ── GRAND TOTAL ROW ── */}
            <TotalRow>
              <TableCell colSpan={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingUpIcon sx={{ fontSize: 16 }} />
                  Grand Total
                </Box>
              </TableCell>
              <TableCell colSpan={6} />
              <TableCell>
                <Typography fontWeight={800} sx={{ fontFamily: FONT.mono, fontSize: 14 }}>
                  ₹{formatCurrency(grandPP)}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography fontWeight={800} sx={{ fontFamily: FONT.mono, fontSize: 14 }}>
                  ₹{formatCurrency(grandSP)}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography fontWeight={800} sx={{
                  color: grandMg >= 0 ? COLORS.success : COLORS.danger,
                  fontFamily: FONT.mono, fontSize: 14,
                }}>
                  ₹{formatCurrency(grandMg)}
                </Typography>
              </TableCell>
              <TableCell>
                <MarginChip percent={grandPct} />
              </TableCell>

              {/**All line items - Total PP Incl GST */}
                  <TableCell>
                      <Typography fontWeight={800} sx={{ fontFamily: FONT.mono, fontSize: 14 }}>
                        ₹{formatCurrency(grandPPGST)}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography fontWeight={800} sx={{ fontFamily: FONT.mono, fontSize: 14 }}>
                        ₹{formatCurrency(grandSPGST)}
                      </Typography>
                    </TableCell>
                
              <TableCell colSpan={viewOnly ? 2 : 3} />
            </TotalRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* ── TABLE FOOTER ── */}
      {!viewOnly && (
        <Box sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          px: 2.5, py: 1.5, borderTop: `1px solid ${COLORS.border}`,
        }}>
          <Typography variant="caption" sx={{ color: COLORS.textMuted, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <InfoOutlinedIcon sx={{ fontSize: 14 }} />
            {onOpenCatalog
              ? <>Click <SearchIcon sx={{ fontSize: 13, mx: 0.3, color: COLORS.indigo }} /> in Details to search the product catalog</>
              : 'Fill in all required fields before saving.'
            }
          </Typography>
          <Box sx={{display:"flex"}}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddCircleOutlineIcon />}
            onClick={onAddItem}
            sx={{
              borderColor: COLORS.indigo, color: COLORS.indigo, borderRadius: '8px',
              fontWeight: 700, textTransform: 'none', fontSize: 12,
              '&:hover': { background: alpha(COLORS.indigo, 0.06), borderColor: COLORS.indigo },
            }}
          >
            Add Line Item
          </Button>
          <Button
              variant="outlined"
              startIcon={<AddCircleOutlineIcon />}
              size="small"
              onClick={handleAddCustomRow}
              sx={{
                borderColor: COLORS.indigo,
                color: COLORS.indigo,
                borderRadius: '8px',
                fontWeight: 700,
                textTransform: 'none',
                fontSize: 12,
                margin:"0px 10px"
              }}
            >
              Add Custom Row
            </Button> 
              </Box>
        </Box>
      )}
    </Box>
  );
};

export default LineItemsTable;